import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
    Input,
    Alert,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faMapMarkerAlt,
    faPhone,
    faEnvelope,
    faUser,
    faCalendarAlt,
    faPaperclip,
    faTag,
    faExclamationTriangle,
    faCheckCircle,
    faTimesCircle,
    faClock
} from "@fortawesome/free-solid-svg-icons";
import featuresService from '../../services/api/features-service';
import adminGrievanceService from "../../services/api/grievance-service";
import ProjectContext from "../../ProjectContext";
import UserContext from "../../UserContext";

const REJECTION_REASONS = [
    { label: "અપમાનજનક, ધમકીભરી અથવા અશ્લીલ ભાષા ધરાવે છે.", value: "abusive_or_offensive_language" },
    { label: "હિંસક, ખલેલ પહોંચાડતી ગ્રાફિક અથવા અયોગ્ય છબીઓ/વીડિયોનો સમાવેશ થાય છે.", value: "graphic_or_inappropriate_media" },
    { label: "મુદ્દાથી સંબંધિત ન હોય તેવા ફોટા/વીડિયો અપલોડ કર્યા છે.", value: "irrelevant_media" },
    { label: "ફરિયાદ કોઈ જાહેર મુદ્દા કે સમુદાયની સમસ્યા સાથે સંબંધિત નથી.", value: "not_public_issue" },
    { label: "માહિતી અસ્પષ્ટ, અપૂર્ણ અથવા મહત્ત્વપૂર્ણ વિગતો ખૂટે છે.", value: "unclear_or_incomplete" },
    { label: "ખોટા દાવા, ગેરમાર્ગે દોરનારી અથવા એવી માહિતી જેની ચકાસણી કરી શકાતી નથી.", value: "unverified_or_false_information" },
    { label: "આ વ્યક્તિગત/પારિવારિક વિવાદ છે, જાહેર બાબત સાથે લેવાદેવા નથી.", value: "personal_or_family_dispute" },
    { label: "આ ફરિયાદ ગુજરાત બહારના મુદ્દા વિશે છે.", value: "outside_coverage_area" },
    { label: "આ ફરિયાદ/રજૂઆત અગાઉ થઈ ચૂકી છે.", value: "duplicate" },
    { label: "સ્પામ માહિતી, પ્રમોશનલ માહિતી અથવા જાહેરાતોનો સમાવેશ થાય છે.", value: "spam_or_promotional" },
    { label: "ખોટી શ્રેણી અથવા સંબંધિત વિષયમાં સબમિટ કરેલ છે.", value: "wrong_category_or_topic" },
    { label: "કોપીરાઈટ કરેલી અથવા ચોરાયેલ ફોટો/મહિતી ધરાવે છે.", value: "copyright_or_stolen_content" },
    { label: "સંમતિ વિના કોઈપણ વ્યક્તિની ગોપનીયતાનું ઉલ્લંઘન કરે છે.", value: "privacy_violation" },
    { label: "આ સમસ્યા પહેલાથી જ ઉકેલાઈ ગઈ છે.", value: "already_resolved" },
    { label: "રાજકીય પ્રચાર/પ્રોપેગેન્ડા દર્શાવતી માહિતી", value: "political_propaganda" },
    { label: "અન્ય (Other)", value: "Other" },
];

const GrievanceDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const project = useContext(ProjectContext);
    const rawUserCtx = useContext(UserContext);
    const projectId = project?.id;

    if (projectId === '') {
        history.push("/project/allprojects");
    }

    const currentUser = rawUserCtx?.userdetails || rawUserCtx;
    const rolesFromProject = project?.current_user_roles || [];
    const rolesFromUser = currentUser?.current_user_roles || currentUser?.roles || [];
    const userRoles = rolesFromProject.length ? rolesFromProject : rolesFromUser;

    let accessLevel = "admin";
    if (userRoles.includes("admin") || userRoles.includes("owner")) {
        accessLevel = "admin";
    } else if (userRoles.includes("accounts")) {
        accessLevel = "accounts";
    } else if (userRoles.includes("editor")) {
        accessLevel = "editor";
    }

    const canModerate = accessLevel === "admin" || accessLevel === "accounts";

    const [grievance, setGrievance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Rejection Dropdown State
    const [rejectionDropdownOpen, setRejectionDropdownOpen] = useState(false);
    const toggleRejectionDropdown = () => setRejectionDropdownOpen(prevState => !prevState);

    // Rejection Modal State
    const [customMsgModalOpen, setCustomMsgModalOpen] = useState(false);
    const [customReason, setCustomReason] = useState("");

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        type: "",
        title: "",
        message: "",
        btnColor: "",
        btnText: ""
    });

    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        const init = async () => {
            if (projectId) {
                try {
                    const featureStatus = await featuresService.getFeatureStatus('enable_grievance_management', projectId);
                    if (featureStatus?.data?.enable_grievance_management === false) {
                        history.push("/dashboard/grievance-management");
                        return;
                    }
                    if (id) fetchDetails();
                } catch (err) {
                    if (id) fetchDetails();
                }
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, projectId]);

    useEffect(() => {
        if (rejectionDropdownOpen || customMsgModalOpen || confirmModal.open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [rejectionDropdownOpen, customMsgModalOpen, confirmModal.open]);

    const fetchDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminGrievanceService.getGrievanceDetails(projectId, id);
            if (res && res.status) {
                setGrievance(res.data);
            } else {
                setError("Grievance not found.");
            }
        } catch (err) {
            setError("Failed to load details.");
        } finally {
            setLoading(false);
        }
    };

    const triggerConfirmation = (type) => {
        if (type === "accept") {
            setConfirmModal({
                open: true,
                type: "accept",
                title: "Approve Grievance",
                message: "Are you sure you want to approve and publish this grievance?",
                btnColor: "success",
                btnText: "Approve"
            });
        } else if (type === "resolve") {
            setConfirmModal({
                open: true,
                type: "resolve",
                title: "Mark as Resolved",
                message: "Are you sure you want to mark this grievance as resolved?",
                btnColor: "info",
                btnText: "Resolve"
            });
        }
    };

    const handleConfirmSubmit = () => {
        performDecision(confirmModal.type);
    };

    const handleRejectOptionClick = (reasonValue) => {
        if (reasonValue === "Other") {
            setCustomMsgModalOpen(true);
            setCustomReason("");
        } else {
            performDecision("reject", reasonValue);
        }
    };

    const handleCustomRejectSubmit = () => {
        if (!customReason.trim()) return;
        performDecision("reject", customReason.trim());
    };

    const performDecision = async (status, reason = null) => {
        if (!canModerate) {
            alert("You do not have permission to perform this action.");
            return;
        }

        setActionLoading(true);
        try {
            const res = await adminGrievanceService.takeDecision(projectId, [id], status, reason);
            if (res && res.status) {
                setCustomMsgModalOpen(false);
                setRejectionDropdownOpen(false);
                setConfirmModal({ ...confirmModal, open: false });
                setCustomReason("");
                await fetchDetails();
            }
        } catch (err) {
            alert("Action failed.");
        } finally {
            setActionLoading(false);
        }
    };

    // --- STYLES ---
    const sectionHeaderStyle = {
        fontSize: "1.05rem",
        fontWeight: "700",
        color: "#1f2937",
        letterSpacing: "0.6px",
        textTransform: "uppercase",
        marginBottom: "0.6rem",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: "6px"
    };


    const cardStyle = {
        border: "none",
        borderRadius: "20px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
        background: "#fff",
        marginBottom: "1.5rem",
        overflow: "visible"
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center justify-content-center min-vh-100" style={{ background: "#f3f4f6" }}>
                <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="pt-5 min-vh-100" style={{ background: "#f3f4f6" }}>
                <Container>
                    <Alert color="danger" className="shadow-sm border-0 rounded-lg">{error}</Alert>
                    <Button color="secondary" onClick={() => history.goBack()}>Back</Button>
                </Container>
            </div>
        );
    }

    const status = grievance.status;

    const getStatusConfig = () => {
        switch (status) {
            case "accepted":
                return { color: "#10b981", bg: "#d1fae5", icon: faCheckCircle, label: "Accepted" };
            case "rejected":
                return { color: "#ef4444", bg: "#fee2e2", icon: faTimesCircle, label: "Rejected" };
            case "resolved":
                return { color: "#3b82f6", bg: "#dbeafe", icon: faCheckCircle, label: "Resolved" };
            default:
                return { color: "#f59e0b", bg: "#fef3c7", icon: faClock, label: "Pending Review" };
        }
    };
    const statusConfig = getStatusConfig();

    return (
        <div style={{ minHeight: "100vh", padding: "2rem 0" }}>
            <Container>
                {/* 1. BACK NAVIGATION */}
                <div className="mb-4">
                    <Button
                        color="link"
                        className="p-0 text-decoration-none d-flex align-items-center"
                        style={{ color: "#6b7280", fontWeight: "600" }}
                        onClick={() => history.push("/dashboard/grievance-management")}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" style={{ marginRight: 8 }} />
                        Back to Grievance Management
                    </Button>
                </div>

                <Row className="align-items-start">

                    {/* LEFT SIDE (Main Content) */}
                    <Col lg="8">

                        {status === "rejected" && (
                            <Alert style={{
                                background: "#fff",
                                border: "none",
                                borderRadius: "16px",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                                borderLeft: "4px solid #ef4444",
                                marginBottom: "1.5rem"
                            }}>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "#ef4444", fontSize: "1.5rem", marginRight: "1rem" }} />
                                    <div>
                                        <h6 className="font-weight-bold mb-1" style={{ color: "#1f2937" }}>Rejection Reason</h6>
                                        <small className="text-muted">{grievance.rejection_reason || "No reason specified."}</small>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        <Card style={cardStyle}>
                            <CardBody className="px-4 px-lg-5 pt-3 pb-4">

                                {/* Top Metadata Row */}
                                <div className="d-flex justify-content-between align-items-center mb-2">

                                    <span style={{ fontSize: "0.8rem", fontWeight: "700", color: "#9ca3af", letterSpacing: "1px" }}>
                                        ID: {grievance._id}
                                    </span>
                                    <div style={{
                                        background: statusConfig.bg,
                                        color: statusConfig.color,
                                        padding: "6px 16px",
                                        borderRadius: "50px",
                                        fontSize: "0.85rem",
                                        fontWeight: "700",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px"
                                    }}>
                                        <FontAwesomeIcon icon={statusConfig.icon} />
                                        {statusConfig.label}
                                    </div>
                                </div>

                                {/* Title Section with Heading */}
                                <div className="mb-3">
                                    <div style={sectionHeaderStyle}>Title</div>
                                    <h2 style={{ fontSize: "1.3rem", fontWeight: "500", color: "#5a606dff", lineHeight: "1.3", margin: 0 }}>
                                        {grievance.title}
                                    </h2>

                                    <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: "10px" }}>
                                        <div className="d-inline-flex align-items-center px-3 py-1 rounded-pill" style={{ background: "#f3f4f6" }}>
                                            <FontAwesomeIcon icon={faTag} className="text-warning mr-2 small" style={{ marginRight: 6, marginRight: 5 }} />
                                            <span style={{ fontWeight: "600", color: "#374151", fontSize: "0.85rem" }}>
                                                {grievance.category || "General Issue"}
                                            </span>
                                        </div>
                                        {grievance.sub_category && (
                                            <div className="d-inline-flex align-items-center px-3 py-1 rounded-pill" style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                                                <FontAwesomeIcon icon={faTag} className="text-primary mr-2" style={{ fontSize: "0.75rem", marginRight: 5 }} />
                                                <span style={{ fontWeight: "700", color: "#1d4ed8", fontSize: "0.8rem" }}>
                                                    {grievance.sub_category}
                                                </span>
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center text-muted ml-2">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 small" style={{ marginRight: 6 }} />
                                            <span className="small font-weight-bold">
                                                {grievance.created_at ? new Date(grievance.created_at).toLocaleString() : "Date N/A"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="mb-5 mt-5">
                                    <div style={sectionHeaderStyle}>Description</div>
                                    <div style={{
                                        background: "#f9fafb",
                                        padding: "1rem 1.25rem",
                                        borderRadius: "10px",
                                        borderLeft: "3px solid #667eea"
                                    }}>

                                        <p style={{ color: "#4b5563", fontSize: "0.95rem", lineHeight: "1.6", margin: 0 }}>
                                            {grievance.description || "No description provided."}
                                        </p>
                                    </div>
                                </div>

                                {/* Attachments */}
                                {grievance.attachments && grievance.attachments.length > 0 && (
                                    <div className="mt-5">
                                        <div style={sectionHeaderStyle}>
                                            <FontAwesomeIcon icon={faPaperclip} className="mr-2" style={{ marginRight: 8 }} />
                                            Attachments
                                        </div>
                                        <Row>
                                            {grievance.attachments.map((att, idx) => (
                                                <Col md="4" xs="6" key={idx} className="mb-3">
                                                    <a href={att.url} target="_blank" rel="noopener noreferrer"
                                                        className="d-block border rounded overflow-hidden shadow-sm"
                                                        style={{ height: "140px", transition: "transform 0.2s" }}
                                                        onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
                                                        onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                                                    >
                                                        <img src={att.url} alt={`Evidence ${idx}`} className="w-100 h-100" style={{ objectFit: "cover" }} />
                                                    </a>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    {/* RIGHT SIDE */}
                    <Col lg="4">

                        {canModerate && (status === "pending" || status === "accepted") && (
                            <Card style={cardStyle}>
                                <CardBody className="p-4 text-center">
                                    <div style={{ ...sectionHeaderStyle, borderBottom: 'none', marginBottom: '1.5rem', textAlign: 'center' }}>
                                        Actions
                                    </div>
                                    <hr />
                                    <div className="d-flex justify-content-center align-items-center">
                                        {/* REJECT BUTTON (Updated) */}
                                        <Dropdown
                                            isOpen={rejectionDropdownOpen}
                                            toggle={toggleRejectionDropdown}
                                            direction="down"
                                            style={{ marginRight: '1rem' }}
                                        >
                                            <DropdownToggle
                                                className="rounded-pill font-weight-bold shadow-sm"
                                                style={{
                                                    background: "#fff",
                                                    border: '2px solid #ef4444',
                                                    color: '#ef4444',
                                                    padding: "0.5rem 2rem",
                                                    transition: "all 0.3s ease"
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = "#ef4444";
                                                    e.target.style.color = "#fff";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = "#fff";
                                                    e.target.style.color = "#ef4444";
                                                }}
                                                disabled={actionLoading}
                                            >
                                                Reject
                                            </DropdownToggle>

                                            <DropdownMenu
                                                right
                                                style={{
                                                    minWidth: "350px",
                                                    maxWidth: "90vw",
                                                    maxHeight: "400px",
                                                    overflowY: "auto",
                                                    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                                                    border: "none",
                                                    borderRadius: "12px",
                                                    zIndex: 1050
                                                }}
                                            >
                                                <DropdownItem header style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Select Rejection Reason</DropdownItem>
                                                {REJECTION_REASONS.map((r) => (
                                                    <DropdownItem
                                                        key={r.value}
                                                        onClick={() => handleRejectOptionClick(r.value)}
                                                        style={{ whiteSpace: "normal", padding: "10px 20px", borderBottom: "1px solid #f3f4f6", color: "#374151" }}
                                                    >
                                                        {r.label}
                                                    </DropdownItem>
                                                ))}
                                            </DropdownMenu>
                                        </Dropdown>

                                        {/* APPROVE / RESOLVE BUTTON */}
                                        {status === "pending" ? (
                                            <Button
                                                style={{
                                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                                    border: "none",
                                                    padding: "0.6rem 2rem"
                                                }}
                                                className="rounded-pill font-weight-bold shadow-sm"
                                                disabled={actionLoading}
                                                onClick={() => triggerConfirmation("accept")}
                                            >
                                                Approve
                                            </Button>
                                        ) : (
                                            <Button
                                                style={{
                                                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                                    border: "none",
                                                    padding: "0.6rem 2rem"
                                                }}
                                                className="rounded-pill font-weight-bold shadow-sm"
                                                disabled={actionLoading}
                                                onClick={() => triggerConfirmation("resolve")}
                                            >
                                                Resolve
                                            </Button>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        )}

                        {/* REPORTER CARD */}
                        <Card style={cardStyle}>
                            <CardBody className="p-4">
                                <div style={sectionHeaderStyle}>Reporter Details</div>

                                <div className="d-flex align-items-center mb-4">
                                    <div
                                        className="rounded-circle overflow-hidden shadow-sm d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "50px",
                                            height: "50px",
                                            // Only use the gradient background if there is NO picture
                                            background: grievance.user_profile_pic ? "transparent" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            marginRight: "15px",
                                            flexShrink: 0
                                        }}
                                    >
                                        {grievance.user_profile_pic ? (
                                            <img
                                                src={grievance.user_profile_pic}
                                                alt="Reporter"
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"; }} // Fallback if image link breaks
                                            />
                                        ) : (
                                            <FontAwesomeIcon icon={faUser} style={{ color: "#fff", fontSize: "1.2rem" }} />
                                        )}
                                    </div>
                                    <div>
                                        <span className="text-uppercase text-muted" style={{ fontSize: "10px", letterSpacing: "1px", fontWeight: "700" }}>Reported By</span>
                                        <div className="text-dark" style={{ fontSize: "1.1rem", fontWeight: "700" }}>
                                            {grievance.grievant_name || "Unknown"}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="d-flex align-items-center mb-3">
                                        <div style={{ width: "35px", height: "35px", background: "#f3f4f6", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
                                            <FontAwesomeIcon icon={faPhone} style={{ color: "#667eea" }} />
                                        </div>
                                        <span style={{ fontWeight: "600", color: "#374151" }}>{grievance.mobile_number || "N/A"}</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div style={{ width: "35px", height: "35px", background: "#f3f4f6", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px" }}>
                                            <FontAwesomeIcon icon={faEnvelope} style={{ color: "#667eea" }} />
                                        </div>
                                        <span style={{ fontWeight: "600", color: "#374151", wordBreak: "break-all" }}>{grievance.email || "N/A"}</span>
                                    </div>
                                </div>

                                <hr className="my-4 border-light" />

                                <div className="d-flex align-items-start">
                                    <div style={{ width: "35px", height: "35px", background: "#f3f4f6", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", flexShrink: 0 }}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: "#667eea" }} />
                                    </div>
                                    <div>
                                        <span className="text-uppercase text-muted d-block" style={{ fontSize: "10px", letterSpacing: "1px", fontWeight: "700" }}>Location</span>
                                        <div style={{ fontWeight: "700", color: "#1f2937", marginBottom: "4px" }}>
                                            {grievance.city || "Unknown"}
                                            {grievance.state ? `, ${grievance.state}` : ""}
                                        </div>
                                        {grievance.address && <p className="text-muted mb-0" style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>{grievance.address}</p>}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* MODALS */}
            <Modal isOpen={confirmModal.open} centered toggle={() => setConfirmModal({ ...confirmModal, open: false })}>
                <ModalHeader className="bg-light border-0"><span className="font-weight-bold">{confirmModal.title}</span></ModalHeader>
                <ModalBody className="text-center py-4 text-muted">{confirmModal.message}</ModalBody>
                <ModalFooter className="bg-light border-0 justify-content-center">
                    <Button color="secondary" size="sm" className="rounded-pill px-4" onClick={() => setConfirmModal({ ...confirmModal, open: false })}>Cancel</Button>
                    <Button color={confirmModal.btnColor} size="sm" className="rounded-pill px-4" onClick={handleConfirmSubmit}>{confirmModal.btnText}</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={customMsgModalOpen} centered toggle={() => setCustomMsgModalOpen(false)}>
                <ModalHeader className="bg-light"><span className="font-weight-bold">Reject Grievance</span></ModalHeader>
                <ModalBody className="p-4">
                    <FormGroup>
                        <Label className="font-weight-bold small text-uppercase text-muted">Reason for rejection</Label>
                        <Input type="textarea" rows="4" placeholder="Type reason here..." value={customReason} onChange={(e) => setCustomReason(e.target.value)} />
                    </FormGroup>
                </ModalBody>
                <ModalFooter className="bg-light border-0">
                    <Button color="secondary" size="sm" className="rounded-pill px-3" onClick={() => setCustomMsgModalOpen(false)}>Cancel</Button>
                    <Button color="danger" size="sm" className="rounded-pill px-3" disabled={!customReason.trim()} onClick={handleCustomRejectSubmit}>Reject</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default GrievanceDetails;